/* eslint-disable @next/next/no-img-element */
/* eslint-disable quotes */
/**
 * Step Three: Appointment details
 */
/** @jsxImportSource @emotion/react */
import React, { useState, forwardRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import _, { range, upperFirst, merge, pick, every, filter, some } from 'lodash'
import Image from 'next/image'
import {
  formatTimeShort,
  shiftDate,
  googleTagManagerEvent,
  segmentEvent,
  googleAnalyticsEvent,
  hasMultiServiceRequests,
  formatBookingRetryButton,
  useBooleanState,
  getArrivalWindow,
  getAppointmentEndTime
} from '../../../helpers'
import { isAuthenticated } from '../../../services'
import {
  useAppointmentAvailabilitySearch,
  useAllMarketServices
} from '../../../services/GraphQlClientService'
import { DateTime } from 'luxon'
import ArrowDown from '../../../../public/images/arrow-down-red.svg'
import SignInText from '../../../components/sign-up-sign-in-form/SignInText'
import Label from './components/Label'
// import Input from './components/Input'
import Input from '../../Input'
// import Button from './components/Button'
import Textarea from './components/Textarea'
import AddressView, { ADDRESS_VIEW_MODE } from './components/AddressView'
import LoadingDot from './components/LoadingDot'
import { Header2 } from '../../../styles/Components'
import {
  DateTimeSection,
  AddressDetailsSection,
  OptionCss
} from './styles/three'
import Button from '../../../components/Button'

import 'react-datepicker/dist/react-datepicker.css'
import CalendarDropdown from '../../CalendarDropdown'

// custom input must forward ref
export const DateTimeCustomInput = forwardRef(
  ({ value, onClick, timezone }, ref) => {
    const dateTime = DateTime.fromFormat(value, 'MM/dd/yyyy', {
      zone: timezone
    })

    const daysAway = dateTime
      .startOf('day')
      .diff(DateTime.local().startOf('day'), 'days').days

    const formattedDate =
      daysAway < 2
        ? upperFirst(dateTime.toRelativeCalendar()) +
        dateTime.toFormat(', MMMM d')
        : dateTime.toFormat('EEEE, MMMM d')
    return (
      <span
        aria-label="selected-date"
        role="button"
        tabIndex={0}
        onKeyPress={e => (e.key === 'Enter' ? onClick() : false)}
        {...{ onClick, ref }}
      >
        {formattedDate}
      </span>
    )
  }
)

const AvailabilitySearchStatus = ({
  availabilityResults, // all availability results
  retry, // filtered list of services to retry if multi-service search returns no results
  availabilityError, // availability error, if present
  exactAvailabilityResult, // appointment exactly matching the start time selected
  alternateAvailabilityResults, //
  isStartDateTimeSelected, //
  timezone,
  setSelectedAppointment,
  serviceRequests,
  dispatch,
  marketServices
}) => {

  if (!isStartDateTimeSelected) {
    return null
  }

  if (!(availabilityResults || availabilityError)) {
    return (
      <div className="availability-search-status pending">
        <LoadingDot margin="0 15px 0 0" size={14} />
        Checking availability
      </div>
    )
  }

  let statusMessage = availabilityError
    ? availabilityError.message?.replace(/^.*?:/, '')?.trim()
    : `Looks like we're not available at this time. Please try another time.`
  let statusClass = 'error'
  let alternateStartOptions = ''

  let arrivalWindow 
  let appointmentWrapUpText = ''

  const hasSomeAddOns = some(serviceRequests, request => request.addOnServiceIds.length)
  const hasMultipleServices = hasMultiServiceRequests(serviceRequests)
  const team = _.chain(availabilityResults?.[0]?.appointmentServices || [])
    .map('providerId')
    .uniq()
    .value()
    .length || 1

  // if there are no results and there are add-ons or partial availability,
  // change status message
  if ((availabilityResults && !availabilityResults.length) && (hasSomeAddOns || hasMultiServiceRequests)) {
    statusMessage = `Looks like we're not available at this time.`
  }

  // check if we had a service that was not available at the time
  if (retry?.alternateServiceSearch?.serviceRequests?.length) {

    const alternateServiceSearch = retry.alternateServiceSearch
    const alternateServiceRequests = alternateServiceSearch.serviceRequests;
    const retryWithAlternateServices = () => {
      dispatch({
        serviceRequests: alternateServiceRequests
      })
      googleAnalyticsEvent('search_alternate_service', { serviceRequests: alternateServiceRequests })
      segmentEvent('search_alternate_service', { serviceRequests: alternateServiceRequests })
    }

    // find the alternate service to dsiplay its name
    const alternateServiceId = alternateServiceSearch?.alternateServiceId
    const alternateMarketService = marketServices.find(ms => ms.service?.id === alternateServiceId);

    if (alternateMarketService) {
      const buttonText = `Book ${alternateMarketService?.service?.name} instead`
      statusMessage = alternateServiceSearch.message;

      alternateStartOptions = [
        ...alternateStartOptions,
        <Button color='#000' outline key='les-services' onClick={retryWithAlternateServices} className='alternate-time'>
          {buttonText}
        </Button>
      ]
    }
  }

  // if there are no results from search and at least one service has Add-ons
  // remove addons from service and check availability
  if ((availabilityResults && !availabilityResults.length) && hasSomeAddOns && (retry && retry.noAddonSearch.length)) {
    const noAddOnSearch = () => {
      dispatch({
        serviceRequests: retry.noAddonSearch
      })

      googleAnalyticsEvent('search_remove_add_ons', { serviceRequests: retry.noAddonSearch })
      segmentEvent('search_remove_add_ons', { serviceRequests: retry.noAddonSearch  })
    }

    alternateStartOptions = [
      ...alternateStartOptions,
      <Button color='#000' outline onClick={noAddOnSearch} key='noaddon' className='alternate-time'>
        Try Again Without Add-ons
      </Button>
    ]
    // if the results return a retry array with partial service availability, re-run search
  } else if ((availabilityResults && !availabilityResults.length) && hasMultipleServices && (retry && retry.singleServiceSearch.length)) {

    const retryLimitedSearch = () => {
      dispatch({
        serviceRequests: retry.singleServiceSearch
      })

      googleAnalyticsEvent('search_remove_services', { serviceRequests: retry.singleServiceSearch })
      segmentEvent('search_remove_services', { serviceRequests: retry.singleServiceSearch })
    }

    const buttonText = formatBookingRetryButton(serviceRequests, retry.singleServiceSearch, marketServices)

    alternateStartOptions = [
      ...alternateStartOptions,
      <Button color='#000' outline key='les-services' onClick={retryLimitedSearch} className='alternate-time'>
        {buttonText}
      </Button>
    ]
  }

  if (availabilityResults && availabilityResults.length) {
    statusClass = 'success'
    statusMessage = `We're available!`

    arrivalWindow = getArrivalWindow(availabilityResults[0].startDateTime, timezone)
    const lastServiceEnd = getAppointmentEndTime(availabilityResults[0], timezone)
    appointmentWrapUpText = `We'll have you ready by ${lastServiceEnd}`

    if (!exactAvailabilityResult) {
      statusMessage =
        `Looks like we're not available at this time. How about one of these?`

      const selectAlternateAppointmentAtIndex = i =>
        setSelectedAppointment(alternateAvailabilityResults[i])

      alternateStartOptions = alternateAvailabilityResults.map(
        ({ actualStartDateTime }, i) => (
          <Button
            outline
            color='#000'
            key={i}
            className="alternate-time"
            onClick={() => selectAlternateAppointmentAtIndex(i)}
          >
            {formatTimeShort(actualStartDateTime, timezone)}
          </Button>
        )
      )
    }
  }

  return (
    <>
      <div className={`availability-search-status ${statusClass}`}>
        {statusMessage}
      </div>

      {exactAvailabilityResult && availabilityResults && availabilityResults.length ? (
        <div className='arrival-window-content'>
          <div className='arrival-window-text'>
            Your Beauty Pro{team > 1 ? 's' : ''} will arrive between <b>{arrivalWindow[0]}</b> and <b>{arrivalWindow[1]}</b>
          </div>

          <div className='wrap-up-text'>
            {appointmentWrapUpText}
          </div>
        </div>
      ) : null }

      {alternateStartOptions && (
        <div className='alternate-time-buttons'>{alternateStartOptions}</div>
      )}
    </>
  )
}

const Three = ({
  state: {
    address = {},
    market: {
      id,
      timezone,
      minimumAdvanceBookingMinutes,
      firstAppointmentTime,
      lastAppointmentTime
    } = {},
    selectedAppointment,
    serviceRequests,
    startDateTime,
    isDesktop
  },
  me,
  dispatch,
  setIsStepValid,
  step
}) => {
  const loggedIn = isAuthenticated()
  // const [requiredVaccine, setRequiredVaccine] = useState(false)
  const [requiredVaccine] = useState(false)
  const [calendarOpen, toggleCalendar] = useBooleanState()
  const marketServices = useAllMarketServices()

  const now = DateTime.local().setZone(timezone)
  if (!startDateTime || startDateTime < now) {
    startDateTime = now.startOf('day')
  }

  // appointment start date and time
  const setStartDateTime = startDateTime => {
    dispatch({ startDateTime })
  }

  // make sure our start date time has the same zone as our market
  if (timezone && startDateTime.zone.name !== timezone) {
    setStartDateTime(startDateTime.setZone(timezone))
  }

  // select an appointment and ensure the selected start time matches
  const setSelectedAppointment = appointment => {
    const selectedStartDateTime =
      selectedAppointment && selectedAppointment.actualStartDateTime

    if (
      !(
        appointment && appointment.actualStartDateTime !== selectedStartDateTime
      )
    ) {
      return
    }

    return dispatch({
      selectedAppointment: appointment,
      startDateTime: DateTime.fromSeconds(
        appointment.actualStartDateTime
      ).setZone(timezone)
    })
  }

  // set the start date time in seconds
  const setStartDateTimeInSeconds = e => {
    const newStartDateTime = DateTime.fromSeconds(+e.target.value).setZone(
      timezone
    )

    setStartDateTime(newStartDateTime)
  }

  // called by date picker to set the date component only of the start
  const setStartDate = date => {
    // update start date time w/ new date with time component copied in
    const newStartDateTime = DateTime.fromJSDate(date)
      .setZone(timezone)
      .startOf('day')
      // copy current time component:
      .set({
        hour: startDateTime.hour,
        minute: startDateTime.minute
      })

    setStartDateTime(newStartDateTime)
  }

  const getSavedAddressesInMarket = () => {
    let savedAddresses = []

    if (me && me.addresses) {
      savedAddresses = filter(
        me.addresses,
        addr => !!addr.marketId && addr.marketId === id && addr.id !== address.id
      )
    }

    savedAddresses.unshift(address)

    return savedAddresses
  }

  const savedAddresses = getSavedAddressesInMarket()
  const [addresses, setAddresses] = useState(savedAddresses)

  //In case of login on this step
  if (savedAddresses.length > addresses.length) {
    setAddresses(savedAddresses)
  }

  const {
    pets: addressPets,
    type: addressType,
    notes: addressNotes,
    apartment: addressApartment
  } = address

  const setAddressType = type => dispatch({ address: { type } })
  const setAddressPets = pets => dispatch({ address: { pets } })
  const setAddressNotes = notes => dispatch({ address: { notes } })
  const setAddressApartment = apartment => dispatch({ address: { apartment } })

  // valid if we have an appointment and a selection for address type and pets
  setIsStepValid(
    every([selectedAppointment, addressType, ...((!!addressType && addressType === 'Home') ? [addressPets] : [])]),
    'Account Details'
  )

  const Option = ({ type, name, disabled }) => {
    let handleClick, cn

    switch (type) {
      case 'location':
        cn = name === addressType ? 'active' : null
        handleClick = () => !disabled && setAddressType(name)
        break
      case 'pets':
        cn = name === addressPets ? 'active' : null
        handleClick = () => !disabled && setAddressPets(name)
        break
      default:
        break
    }

    return (
      <div
        aria-label={name}
        role="button"
        tabIndex={0}
        onKeyPress={e => (e.key === 'Enter' ? handleClick() : false)}
        css={OptionCss}
        className={cn}
        onClick={handleClick}
      >
        {/* {OPTION_ICONS[type][name]} */}
        {/* {optionsIcons(type, name, address)} */}
        <span>{name}</span>
      </div>
    )
  }

  // generate array of minutes from markets allowed firstAppointmentTime - lastAppointmentTime
  // offset by 30 minutes, added 30 to the lastAppointmentTime to fit the last time slot
  // range returns up to the last item
  const timeOptionsArray = range(firstAppointmentTime, lastAppointmentTime + 30, 30)

  // build start time options
  const startDateTimeOptions = timeOptionsArray
    .map(minutes => startDateTime.startOf('day').plus({ minutes }))
    .filter(time => time >= now.plus({ minutes: minimumAdvanceBookingMinutes })) // make sure they all start in the future
    .map((startDateTimeOption, index) => (
      <option key={index} value={startDateTimeOption.toSeconds()}>
        {startDateTimeOption.toFormat('hh:mm a')}
      </option>
    ))

  const marketServicesForMarket = filter(marketServices, ms => ms.marketId === id)

  const [availabilityResults, availabilityError, retry] =
    useAppointmentAvailabilitySearch(
      dispatch,
      startDateTime.toJSDate(),
      timezone,
      8 * 60, // two hours in either direction
      30,
      pick(address, ['street', 'city', 'state', 'zip', 'type']),
      serviceRequests.map(sr =>
        merge(
          {},
          {
            backToBack: false,
            instances: 1
          },
          sr
        )
      ),
      requiredVaccine
        ? {
          'customAttributes.covid19Vaccinated': true
        }
        : null,
      marketServicesForMarket
    )

  const isStartDateTimeSelected = !!startDateTime.hour

  useEffect(() => {
    if (!isStartDateTimeSelected) {
      return
    }
    googleTagManagerEvent('search')
  }, [isStartDateTimeSelected])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const startDateTimeInSeconds = startDateTime.toSeconds()

  const exactAvailabilityResult =
    availabilityResults &&
    availabilityResults.find(
      a => a.actualStartDateTime === startDateTimeInSeconds
    )
  const alternateAvailabilityResults = availabilityResults || []

  // select the exact match if we found one
  setSelectedAppointment(exactAvailabilityResult)

  return (
    <>
      <DateTimeSection>
        <Header2 
          align="center" 
          margin="89px 0 44px"
          fontSize={48}
        >
          When do you want to book?
        </Header2>
        <div className='date-and-time'>
          <div className='date'>
            <CalendarDropdown 
              date={shiftDate(startDateTime, timezone, -1)}
              onClick={date => {
                setStartDate(shiftDate(date, timezone, 1))
                toggleCalendar()
              }}
              open={calendarOpen}
              toggle={toggleCalendar}
              timezone={timezone}
              format
            />
          </div>
          <div className="time">
            <select
              aria-label="selected-time"
              value={startDateTime.toSeconds()}
              onChange={setStartDateTimeInSeconds}
            >
              <option
                value=""
                disabled={isStartDateTimeSelected}
                hidden={isStartDateTimeSelected}
              >
                Select Time
              </option>
              {startDateTimeOptions}
            </select>
            <Image src={ArrowDown} alt="arrow-down" />
          </div>
        </div>

        <AvailabilitySearchStatus
          availabilityResults={availabilityResults}
          retry={retry}
          availabilityError={availabilityError}
          exactAvailabilityResult={exactAvailabilityResult}
          alternateAvailabilityResults={alternateAvailabilityResults}
          timezone={timezone}
          isStartDateTimeSelected={isStartDateTimeSelected}
          setSelectedAppointment={setSelectedAppointment}
          dispatch={dispatch}
          serviceRequests={serviceRequests}
          marketServices={marketServices}
        />
      </DateTimeSection>
      <AddressDetailsSection disabled={!selectedAppointment}>
        <div className='inner-container'>
          <Header2 
            align="center"
            fontSize={48}
            lineHeight='138%'
            margin={`0 0 ${loggedIn ? '' : '-'}42px 0`}
          >
            The final details
          </Header2>
          <SignInText
            dispatch={dispatch}
            disabled={!selectedAppointment}
            isDesktop={isDesktop}
          >
            Already a member?
          </SignInText>
          <div className='address-details' style={!loggedIn ? { marginTop: '40px' } : {}}>
            <AddressView
              {...{
                address,
                addresses,
                dispatch,
                mode: ADDRESS_VIEW_MODE.DISPLAY,
                selectedAppointment,
                step
              }}
            />
            <div className="container">
              <Input
                placeholder="Apt, Unit, Room #"
                onChange={({ target: { value } }) => setAddressApartment(value)}
                value={addressApartment}
                disabled={!selectedAppointment}
              />
              <Label 
                htmlFor="directions"
                className='location-pets-label'
              >
                Any special directions?
              </Label>
              <Textarea
                id="directions"
                rows="3"
                placeholder="Building security? Hard to find? Tell us here."
                onChange={({ target: { value } }) => setAddressNotes(value)}
                value={addressNotes}
                disabled={!selectedAppointment}
              />
              <Label className='location-pets-label'>What type of location is this?*</Label>
              <div className="option-group">
                <Option
                  type="location"
                  name="Home"
                  disabled={!selectedAppointment}
                />
                <Option
                  type="location"
                  name="Hotel"
                  disabled={!selectedAppointment}
                />
                <Option
                  type="location"
                  name="Office"
                  disabled={!selectedAppointment}
                />
                <Option
                  type="location"
                  name="Other"
                  disabled={!selectedAppointment}
                />
              </div>
              { addressType === 'Home' && (
                <>
                  <Label className='location-pets-label'>Do you have pets?*</Label>
                  <div className="option-group">
                    <Option
                      type="pets"
                      name="Dog"
                      disabled={!selectedAppointment}
                    />
                    <Option
                      type="pets"
                      name="Cat"
                      disabled={!selectedAppointment}
                    />
                    <Option
                      type="pets"
                      name="Both"
                      disabled={!selectedAppointment}
                    />
                    <Option
                      type="pets"
                      name="None"
                      disabled={!selectedAppointment}
                    />
                  </div>
                </>
              )}
            </div>
            <Label style={{ marginTop: '24px' }}>*Required Field</Label>
          </div>
        </div>
      </AddressDetailsSection>
    </>
  )
}

export default Three

Three.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  dispatchValueAtKeyPath: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired
}
